// File: src/hooks/useAddProject.js

import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { Api_Url } from "../../utils/ApiUrl";
import { REFUND_PROJECT_DATA } from "../../utils/Constants";
import { useGetData } from "../../contexts/GetDataContext";

export function useAddProject({ isEditMode, editData, onEditComplete, data }) {
    const [showAddModal, setShowAddModal] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [formData, setFormData] = useState({
        selectedProject: null,
        refundLimit: "",
        refundLimitPerUser: "",
    });
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const getData = useGetData();

    // Create a Set of existing privateSales
    const existingPrivateSales = data
        ? new Set(data.map((item) => item.privateSale))
        : new Set([editData?.privateSale]);

    // Filter projects
    const filteredProjects = data
        ? REFUND_PROJECT_DATA.filter((project) => !existingPrivateSales.has(project.value))
        : REFUND_PROJECT_DATA.filter((project) => existingPrivateSales.has(project.value));

    useEffect(() => {
        if (isEditMode && editData) {
            const selectedProjectObj = filteredProjects.find(
                (project) => project.value === editData.privateSale
            );
            setFormData({
                selectedProject: selectedProjectObj || null,
                refundLimit: editData.refundLimit.toString(),
                refundLimitPerUser: editData.refundLimitPerUser.toString(),
            });
            setShowAddModal(true);
        }
    }, [isEditMode, editData]);

    const handleOpenAddModal = () => setShowAddModal(true);
    const handleCloseAddModal = () => {
        setShowAddModal(false);
        if (isEditMode) onEditComplete();
    };

    const handleOpenSuccessModal = () => setShowSuccessModal(true);
    const handleCloseSuccessModal = () => {
        setShowSuccessModal(false);
        if (isEditMode) onEditComplete();
    };

    const toggleDropdown = () => setIsDropdownOpen(!isDropdownOpen);

    const handleProjectSelect = (project) => {
        setFormData({ ...formData, selectedProject: project });
        setIsDropdownOpen(false);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name.startsWith("refundLimit") && (!isNaN(value) && (value === "" || parseInt(value) >= 0))) {
            setFormData({ ...formData, [name]: value });
        } else if (name === "selectedProject") {
            setFormData({ ...formData, [name]: value });
        }
    };

    const submitProject = async (payload) => {
        try {
            const endpoint = isEditMode ? `${Api_Url}/credit/edit` : `${Api_Url}/credit`;
            if (isEditMode && editData) {
                payload.id = editData._id;
                delete payload.privateSale;
            }

            const config = {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
                },
            };

            let response;
            if (isEditMode) {
                response = await axios.patch(endpoint, payload, config);
            } else {
                response = await axios.post(endpoint, payload, config);
            }

            if (response.status === 200 || response.status === 201) {
                setIsProcessing(false);
                handleCloseAddModal();
                handleOpenSuccessModal();
                setFormData({ selectedProject: null, refundLimit: "", refundLimitPerUser: "" });
                getData();
                return true;
            } else {
                setIsProcessing(false);
                toast.error(`Failed to ${isEditMode ? "update" : "add"} project: ${response.data?.message || "Something went wrong"}`);
                console.error(`Failed to ${isEditMode ? "update" : "add"} project:`, response);
                return false;
            }
        } catch (error) {
            setIsProcessing(false);
            toast.error(`Error ${isEditMode ? "updating" : "adding"} project: ${error.message}`);
            console.error(`Error ${isEditMode ? "updating" : "adding"} project:`, error);
            return false;
        }
    };

    const handleSubmit = async () => {
        const { selectedProject, refundLimit, refundLimitPerUser } = formData;

        if (!selectedProject) {
            toast.warn("Please select a project.");
            return;
        }
        if (refundLimit === "") {
            toast.warn("Please enter the refund limit.");
            return;
        }
        if (refundLimitPerUser === "") {
            toast.warn("Please enter the refund limit per user.");
            return;
        }

        const parsedRefundLimit = parseInt(refundLimit);
        const parsedRefundLimitPerUser = parseInt(refundLimitPerUser);

        if (parsedRefundLimit <= 0 || parsedRefundLimitPerUser <= 0) {
            toast.error("Refund limits must be at least 1.");
            return;
        }
        if (parsedRefundLimit <= parsedRefundLimitPerUser) {
            toast.error("Refund limit must be greater than the refund limit per user.");
            return;
        }

        setIsProcessing(true);
        const payload = {
            privateSale: selectedProject.value,
            refundLimit: parsedRefundLimit,
            refundLimitPerUser: parsedRefundLimitPerUser,
            isActive: true,
        };
        await submitProject(payload);
    };

    return {
        showAddModal,
        showSuccessModal,
        formData,
        isDropdownOpen,
        isProcessing,
        filteredProjects,
        handleOpenAddModal,
        handleCloseAddModal,
        handleOpenSuccessModal,
        handleCloseSuccessModal,
        toggleDropdown,
        handleProjectSelect,
        handleInputChange,
        handleSubmit,
    };
}
